import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@mondaygpt/constants'
import { AccessTokenPayload } from '@mondaygpt/types'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { jwtDecode } from 'jwt-decode'
import {
  ApiError,
  AuthenticationService,
  MyUserDto,
  OpenAPI,
  RefreshTokenDto,
  VerifyOtpDto,
  VerifyOtpResponseDto,
} from '../../../openapi'
import { apiCall, getDeviceId } from '../../core'
import { broadcastLogout } from '../utils/auth-broadcast'

interface AuthenticationViewState {
  isLoggedIn: boolean
  /** In-memory access token. Never persisted, never written to localStorage. */
  accessToken: string | null
  userId: string | null
  organizationId: string | null
  currentEmail: string | null // Store email for OTP verification
  /** True until silent refresh against the cookie has resolved one way or another. */
  isHydrating: boolean
  otpState: {
    isRequesting: boolean
    otpSent: boolean
    error: string | null
  }
  verificationState: {
    isVerifying: boolean
    error: string | null
  }
}

const initialState: AuthenticationViewState = {
  isLoggedIn: false,
  accessToken: null,
  userId: null,
  organizationId: null,
  currentEmail: null,
  isHydrating: true,
  otpState: {
    isRequesting: false,
    otpSent: false,
    error: null,
  },
  verificationState: {
    isVerifying: false,
    error: null,
  },
}

// Async thunks
export const requestOtpAction = createAsyncThunk(
  'authentication/requestOtp',
  async (email: string, { rejectWithValue }) => {
    const result = await apiCall(
      AuthenticationService.authenticationControllerRequestOtpV1,
      rejectWithValue,
      {
        email,
        deviceId: getDeviceId(),
      }
    )
    return { ...result, email }
  }
)

export const verifyOtpAction = createAsyncThunk(
  'authentication/verifyOtp',
  async (verifyOtpDto: VerifyOtpDto, { rejectWithValue }) => {
    const response = (await apiCall(
      AuthenticationService.authenticationControllerVerifyOtpV1,
      rejectWithValue,
      verifyOtpDto
    )) as VerifyOtpResponseDto

    // Validate admin role from JWT token
    if (response.tokens?.accessToken) {
      try {
        const decoded = jwtDecode<AccessTokenPayload>(response.tokens.accessToken)
        const userRole = decoded.role

        if (userRole !== 'admin') {
          throw new Error('Access denied. This system is restricted to administrators only.')
        }
      } catch (error) {
        if (error instanceof Error && error.message.includes('administrators only')) {
          throw error
        }
        throw new Error('Invalid authentication token')
      }
    }

    return response
  }
)

export const fetchCurrentUserAction = createAsyncThunk(
  'authentication/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    return (await apiCall(
      AuthenticationService.authenticationControllerMeV1,
      rejectWithValue
    )) as MyUserDto
  }
)

/**
 * Refresh the authentication token. Web clients send empty refreshToken in body —
 * the server reads the token from the HttpOnly cookie.
 */
export const refreshTokenAction = createAsyncThunk(
  'authentication/refreshToken',
  async (
    { refreshToken, businessId }: Partial<RefreshTokenDto> & { businessId: string },
    { rejectWithValue }
  ) =>
    await apiCall(AuthenticationService.authenticationControllerRefreshTokenV1, rejectWithValue, {
      refreshToken: refreshToken ?? '',
      businessId,
    })
)

/**
 * Boot-time silent refresh. Mirrors apps/client. Deduped via module-level in-flight
 * promise + cross-tab Web Lock so React StrictMode + sibling tabs share one
 * round-trip and don't trip the server's session-rotation race.
 */
let silentRefreshInFlight: Promise<{ accessToken: string }> | null = null
const REFRESH_LOCK_NAME = 'taka-auth-refresh'

async function withRefreshLock<T>(body: () => Promise<T>): Promise<T> {
  const locks = (
    navigator as Navigator & {
      locks?: { request: (name: string, cb: () => Promise<T>) => Promise<T> }
    }
  ).locks
  if (!locks?.request) return body()
  return locks.request(REFRESH_LOCK_NAME, body)
}

export const silentRefreshAction = createAsyncThunk(
  'authentication/silentRefresh',
  async (_: void, { dispatch, rejectWithValue }) => {
    if (silentRefreshInFlight) {
      try {
        return await silentRefreshInFlight
      } catch (error) {
        if (error instanceof ApiError) return rejectWithValue(error.body)
        return rejectWithValue({ message: (error as Error)?.message ?? 'Silent refresh failed' })
      }
    }

    silentRefreshInFlight = (async () => {
      const legacyRefresh = (() => {
        try {
          return localStorage.getItem(REFRESH_TOKEN_KEY) ?? ''
        } catch {
          return ''
        }
      })()
      const businessId = (() => {
        try {
          const access = localStorage.getItem(ACCESS_TOKEN_KEY)
          if (!access) return '-1'
          const decoded = jwtDecode<AccessTokenPayload>(access)
          return decoded.bid || '-1'
        } catch {
          return '-1'
        }
      })()

      return withRefreshLock(async () => {
        const tokens = await AuthenticationService.authenticationControllerRefreshTokenV1({
          refreshToken: legacyRefresh,
          businessId,
        })

        try {
          localStorage.removeItem(ACCESS_TOKEN_KEY)
          localStorage.removeItem(REFRESH_TOKEN_KEY)
        } catch {
          /* swallow */
        }

        const decoded = jwtDecode<AccessTokenPayload>(tokens.accessToken)
        if (decoded.role !== 'admin') {
          throw new Error('Access denied. Admin role required.')
        }

        OpenAPI.TOKEN = tokens.accessToken
        return tokens
      })
    })()

    try {
      const result = await silentRefreshInFlight
      try {
        await dispatch(fetchCurrentUserAction()).unwrap()
      } catch (err) {
        // Tokens are valid; let the UI surface the profile-fetch failure on its own.
        console.warn('birdbrain auth: fetchCurrentUser after silent refresh failed:', err)
      }
      return result
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.body)
      }
      return rejectWithValue({ message: (error as Error)?.message ?? 'Silent refresh failed' })
    } finally {
      silentRefreshInFlight = null
    }
  }
)

/**
 * Module-level guard read by `refreshOnce` (api-error-handler) so a 401-driven
 * refresh can't race a logout and resurrect the cookie/server session.
 */
export let isLoggingOut = false

export function setLoggingOut(value: boolean): void {
  isLoggingOut = value
}

export const logoutAction = createAsyncThunk(
  'authentication/logoutAction',
  async (options: { broadcast?: boolean } | void, { dispatch }) => {
    const broadcast = options?.broadcast !== false

    isLoggingOut = true
    if (broadcast) broadcastLogout()

    // Keep the access token in Redux until /logout has returned — /logout needs
    // a valid Bearer to set the server-side grace marker. See the apps/client
    // slice for the full rationale.
    try {
      await AuthenticationService.authenticationControllerLogoutV1({ refreshToken: '' })
    } catch {
      /* swallow */
    }

    await new Promise(r => setTimeout(r, 200))
    try {
      await AuthenticationService.authenticationControllerLogoutV1({ refreshToken: '' })
    } catch {
      /* swallow */
    }

    dispatch(logout())

    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
    } catch {
      /* swallow */
    }

    isLoggingOut = false
    return { success: true }
  }
)

/**
 * Set authentication state and update API client. In-memory only — no localStorage.
 */
const setAuthenticationState = (state: AuthenticationViewState, accessToken?: string) => {
  const isLoggedIn = !!accessToken
  state.isLoggedIn = isLoggedIn
  state.accessToken = accessToken ?? null

  if (accessToken) {
    try {
      const decoded = jwtDecode<AccessTokenPayload>(accessToken)
      state.userId = decoded.sub
      state.organizationId = null
    } catch (error) {
      console.error('Error decoding JWT token:', error)
      state.isLoggedIn = false
      state.accessToken = null
      state.userId = null
      state.organizationId = null
    }
  } else {
    state.userId = null
    state.organizationId = null
  }

  OpenAPI.TOKEN = accessToken
}

const authenticationViewSlice = createSlice({
  name: 'authenticationView',
  initialState,
  reducers: {
    finishHydration: state => {
      state.isHydrating = false
    },
    syncAccessToken: (state, action: PayloadAction<string>) => {
      if (state.accessToken === action.payload) return
      if (!state.accessToken) return // logged out — don't re-authenticate via stale broadcast
      setAuthenticationState(state, action.payload)
      state.isHydrating = false
    },
    logout: state => {
      setAuthenticationState(state)
      state.currentEmail = null
      state.otpState = { ...initialState.otpState }
      state.verificationState = { ...initialState.verificationState }
      state.isHydrating = false
    },
    clearOtpState: state => {
      state.otpState = initialState.otpState
      state.currentEmail = null
    },
    clearVerificationState: state => {
      state.verificationState = initialState.verificationState
    },
  },
  extraReducers: builder => {
    builder
      .addCase(requestOtpAction.pending, state => {
        state.otpState.isRequesting = true
        state.otpState.error = null
      })
      .addCase(requestOtpAction.fulfilled, (state, action) => {
        state.otpState.isRequesting = false
        state.otpState.otpSent = true
        state.otpState.error = null
        state.currentEmail = (action.payload as any).email
      })
      .addCase(requestOtpAction.rejected, (state, action) => {
        state.otpState.isRequesting = false
        state.otpState.error = (action.payload as any)?.message || 'Failed to send OTP'
      })

    builder
      .addCase(verifyOtpAction.pending, state => {
        state.verificationState.isVerifying = true
        state.verificationState.error = null
      })
      .addCase(verifyOtpAction.fulfilled, (state, action) => {
        const { accessToken } = action.payload.tokens
        setAuthenticationState(state, accessToken)
        state.verificationState.isVerifying = false
        state.verificationState.error = null
        state.currentEmail = null
        state.otpState = { ...initialState.otpState }
        state.isHydrating = false
      })
      .addCase(verifyOtpAction.rejected, (state, action) => {
        state.verificationState.isVerifying = false
        const error = action.payload as any

        if (error?.status === 403 || error?.message?.includes('administrators only')) {
          state.verificationState.error =
            'This system is restricted to administrators only. This request has been logged.'
        } else {
          state.verificationState.error = error?.message || 'This request has been logged.'
        }
      })

    builder
      .addCase(fetchCurrentUserAction.fulfilled, (state, action: PayloadAction<MyUserDto>) => {
        state.userId = action.payload.user.id
        state.organizationId = null
        state.isLoggedIn = true
      })
      .addCase(fetchCurrentUserAction.rejected, state => {
        state.isLoggedIn = false
        state.accessToken = null
        state.userId = null
        state.organizationId = null
      })

    // Guard against logout-races-refresh: if state was cleared by logout while
    // /refresh was in flight, don't re-authenticate the user with the stale result.
    builder.addCase(refreshTokenAction.fulfilled, (state, action) => {
      if (!state.accessToken) return
      setAuthenticationState(state, action.payload.accessToken)
    })

    builder.addCase(silentRefreshAction.fulfilled, (state, action) => {
      const { accessToken } = action.payload
      setAuthenticationState(state, accessToken)
      state.isHydrating = false
    })
    builder.addCase(silentRefreshAction.rejected, state => {
      setAuthenticationState(state)
      state.isHydrating = false
    })
  },
})

export const { finishHydration, logout, clearOtpState, clearVerificationState, syncAccessToken } =
  authenticationViewSlice.actions
export const authenticationViewReducer = authenticationViewSlice.reducer

// Selectors
export const selectOtpState = (state: { authenticationView: AuthenticationViewState }) =>
  state.authenticationView.otpState
export const selectVerificationState = (state: { authenticationView: AuthenticationViewState }) =>
  state.authenticationView.verificationState
export const selectIsLoggedIn = (state: { authenticationView: AuthenticationViewState }) =>
  state.authenticationView.isLoggedIn
export const selectCurrentEmail = (state: { authenticationView: AuthenticationViewState }) =>
  state.authenticationView.currentEmail
export const selectAccessToken = (state: { authenticationView: AuthenticationViewState }) =>
  state.authenticationView.accessToken
export const selectIsHydrating = (state: { authenticationView: AuthenticationViewState }) =>
  state.authenticationView.isHydrating
